import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {RefModelNode, TakeUntilComponentDestroy} from '@q9elements/ui-kit/common';

import {debounceTime, filter, take} from 'rxjs/operators';

import {RmnD3TreeData} from '../models/rmn-d3-tree-data.interface';
import {ImpactAssessmentTreeService} from '../services/impact-analysis-tree.service';
import {ImpactAssessmentStore} from '../services/impact-assessment-store';

@Component({
  selector: 'app-impact-tree',
  templateUrl: './impact-tree.component.html',
  styleUrls: ['./impact-tree.component.scss']
})
export class ImpactTreeComponent extends TakeUntilComponentDestroy implements OnInit, OnDestroy {
  @Input() height = 500;
  @Input() width = 700;
  @Input() treeData: RmnD3TreeData;
  @Output() loadChildren: EventEmitter<any> = new EventEmitter<any>();
  @Output() labelLeftClick: EventEmitter<{nodeId: string; refModelId: string}> = new EventEmitter<{
    nodeId: string;
    refModelId: string;
  }>();
  @Output() labelRightClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() labelHover: EventEmitter<any> = new EventEmitter<any>();
  @Output() labelMouseLeave: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('treeContainer', {static: true}) treeContainer: ElementRef;

  private node: RefModelNode;
  private clickedNode;
  private isMoreChildrenNeeded = false;

  constructor(
    private impactAssessmentStore: ImpactAssessmentStore,
    private impactAssessmentTreeService: ImpactAssessmentTreeService
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToNodeData();
    this.buildTree();
    this.subscribeToTreeData();
    this.subscribeToCircleClick();
    this.subscribeToLabelLeftClick();
    this.subscribeToLabelRightClick();
    this.subscribeToLabelHover();
    this.subscribeToLabelMouseLeave();
  }

  private subscribeToNodeData() {
    this.impactAssessmentStore
      .select('nodeData')
      .pipe(take(1))
      .subscribe((node: RefModelNode) => (this.node = node));
  }

  subscribeToTreeData() {
    this.impactAssessmentStore
      .select('treeData')
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(treeData => !!treeData)
      )
      .subscribe((treeDataChildren: RmnD3TreeData[]) => {
        if (this.isMoreChildrenNeeded) {
          return this.upsertNextPages(treeDataChildren);
        }

        this.upsertChildren(treeDataChildren);
      });
  }

  subscribeToCircleClick() {
    this.impactAssessmentTreeService.onLoadChildren$
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(info => !!(info && info.node))
      )
      .subscribe(info => {
        this.clickedNode = info.node;
        this.isMoreChildrenNeeded = info.isMoreChildrenNeeded;
        return this.loadChildren.emit(info);
      });
  }

  subscribeToLabelLeftClick() {
    this.impactAssessmentTreeService.onLabelLeftClick$
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(node => this.labelLeftClick.emit(node));
  }

  subscribeToLabelRightClick() {
    this.impactAssessmentTreeService.onLabelRightClick$
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(info => this.labelRightClick.emit(info));
  }

  subscribeToLabelHover() {
    this.impactAssessmentTreeService.onLabelHover$
      .pipe(this.takeUntilComponentDestroy(), debounceTime(400))
      .subscribe(node => this.labelHover.emit(node));
  }

  subscribeToLabelMouseLeave() {
    this.impactAssessmentTreeService.onLabelMouseLeave$
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(() => this.labelMouseLeave.emit());
  }

  upsertNextPages(treeDataChildren) {
    const childrenToUpsert = {
      children: [...this.clickedNode.data.children, ...treeDataChildren.children]
    };

    this.clickedNode.data.children = childrenToUpsert.children;
    this.impactAssessmentTreeService.reset(childrenToUpsert);
    this.clickedNode.data.kids = this.impactAssessmentTreeService.mapFetchedData(
      childrenToUpsert,
      'children',
      this.clickedNode
    );
  }

  upsertChildren(treeDataChildren) {
    if (!this.clickedNode) {
      return;
    }

    this.clickedNode.data.children = [...treeDataChildren.children];
    this.impactAssessmentTreeService.reset(treeDataChildren);

    this.clickedNode.children = this.impactAssessmentTreeService.mapFetchedData(
      treeDataChildren,
      'kids',
      this.clickedNode
    );
    this.clickedNode.data.kids = this.impactAssessmentTreeService.mapFetchedData(
      treeDataChildren,
      'children',
      this.clickedNode
    );

    this.impactAssessmentTreeService.updateTree(this.clickedNode);
  }

  buildTree(treeJson = this.treeData) {
    const {width, height} = this;
    const {svg, svgGroup} = this.impactAssessmentTreeService.appendSvgForTree({
      selector: this.treeContainer.nativeElement
    });

    this.impactAssessmentTreeService.setTreeLayout([height, width]);
    this.impactAssessmentTreeService.reset(treeJson);
    this.impactAssessmentTreeService.setTreeRoot(treeJson, {height, width});
    this.impactAssessmentTreeService.applyZoomableBehavior(svg, svgGroup);
    this.impactAssessmentTreeService.setStartPosition();
    this.impactAssessmentTreeService.updateTree();
  }

  ngOnDestroy(): void {
    this.takeUntilComponentDestroy();
  }
}
